<script setup lang="ts">
import RawHtmlRenderer from "~/components/Common/RawHtmlRenderer.vue";
import type { ProductType, CustomFields } from "@/types";

const mainStore = useMainStore();
const cardType = computed(() => mainStore?.globalSettings?.CardType);

type PropsType = {
  product: ProductType;
  ctaButton?: boolean;
  link?: string;
  bestValue?: boolean;
  buyNow?: () => void;
  isChangePlan?: boolean;
};

const props = defineProps<PropsType>();
console.log("buyNow", props.buyNow);
const customFields = computed<CustomFields | undefined>(
  (): CustomFields | undefined => {
    return props.product?.offerServiceTemplate[0]?.serviceTemplate
      ?.customFields;
  }
);

// Check if this is a best value product - MODIFIED to respect prop
const isBestValue = computed(() => {
  // FIRST check if we're in the Change Plan journey - if so, return false immediately
  if (typeof window !== "undefined") {
    const currentUrl = window.location.href || "";
    const isChangePlanJourney =
      currentUrl.includes("/change-plan") ||
      currentUrl.includes("/account/dashboard");
    if (isChangePlanJourney) {
      // Never show best value tag in Change Plan journey
      return false;
    }
  }

  // CRITICAL FIX: Always respect the bestValue prop passed from parent
  // This ensures consistent behavior across home page and change plan
  if (props.bestValue !== undefined) {
    return props.bestValue;
  }

  // Fallback to data-driven approach only if no prop is provided
  return (
    customFields.value?.pim?.goCommerce?.metadataBestValue === "true" ||
    customFields.value?.pim?.goCommerce?.isFeaturedProduct === true
  );
});

// Determine if we should show the Buy Now button
// We hide it in the Change Plan journey or any dashboard context
const shouldShowBuyNowButton = computed(() => {
  if (typeof window === "undefined") return false;

  const currentUrl = window.location.href || "";
  const isChangePlanJourney =
    currentUrl.includes("/change-plan") ||
    currentUrl.includes("/account/dashboard");

  // Check if the buyNow prop exists and we're not in the Change Plan journey
  return typeof props.buyNow === "function" && !isChangePlanJourney;
});

// Get the raw quota data GB from the product
const baseQuotaDataGb = computed(() => {
  return customFields.value?.pim?.goCommerce?.quotaDataGb || 0;
});

// Check if we're in Change Plan context
const isChangePlanContext = computed(() => {
  if (typeof window !== "undefined") {
    const currentUrl = window.location.href || "";
    return (
      currentUrl.includes("/change-plan") ||
      currentUrl.includes("/account/dashboard?view=my-plans") ||
      currentUrl.includes("/account/dashboard") ||
      currentUrl.includes("planchange")
    );
  }
  return false;
});

// Get the bonus data amount in GB (as a number, not a string)
const bonusDataGbNumber = computed(() => {
  // CRITICAL: If we're in the Change Plan context, NEVER show bonus data
  // as it's only for new customers, not for plan changes
  if (isChangePlanContext.value) {
    console.log(
      "[Plan/Card] DISABLING BONUS DATA for Change Plan context:",
      props.product?.name
    );
    return 0;
  }

  // Get allowedDiscountPlans array
  const allowedDiscountPlans = props.product?.allowedDiscountPlans;
  if (!allowedDiscountPlans || !Array.isArray(allowedDiscountPlans)) {
    return 0;
  }

  // Loop through discount plans
  for (const discountPlan of allowedDiscountPlans) {
    // Check the exact structure from the sample JSON
    const discountPlanItems = Array.isArray(discountPlan.discountPlanItem)
      ? discountPlan.discountPlanItem
      : [discountPlan.discountPlanItem];

    for (const item of discountPlanItems) {
      if (
        item?.customFields?.customField &&
        Array.isArray(item.customFields.customField)
      ) {
        // Find the customField with code=bonusDataAmount
        const bonusDataField = item.customFields.customField.find(
          (field) => field.code === "bonusDataAmount"
        );
        if (bonusDataField && bonusDataField.doubleValue) {
          return Math.round(bonusDataField.doubleValue);
        }
      }
    }
  }

  return 0;
});

// Calculate the total data amount (base + bonus)
const quotaDataGb = computed(() => {
  // For Change Plan context, only show the base data amount (no bonus)
  if (isChangePlanContext.value) {
    return baseQuotaDataGb.value;
  }
  // Otherwise, show base + bonus for normal display
  const total = baseQuotaDataGb.value + bonusDataGbNumber.value;
  return total;
});

const productFeatures = computed(() => {
  return props.product?.strapiData?.features || [];
});

// Get promotion description from strapi data
const promotionDescription = computed(() => {
  // CRITICAL: Check if this is product 86 ($30 45GB) and hide promotion in Change Plan journey
  const productObj = props.product || {};
  const productAsAny = productObj as any;

  // Check multiple ways to identify product 86
  const isProduct86 =
    productObj.code === "86" ||
    productAsAny.serviceTemplateCode === "86" ||
    productObj.offerServiceTemplate?.[0]?.serviceTemplate?.code === "86" ||
    productObj.productOfferingPrice?.recurring?.[0]?.id === 86 ||
    productObj.name?.includes("$30 45GB");

  // If we're in the Change Plan journey and this is product 86, return null to hide the promotion
  if (
    isProduct86 &&
    window.location.pathname.includes("/my-plans/change-plan")
  ) {
    console.log(
      "🔴 HIDING promotion description for product 86 in Change Plan journey"
    );
    return "";
  }

  // Default behavior - Get description from Strapi data only
  return props.product?.strapiData?.promotionDescription || "";
});

// Get gift card from strapi product data or fallback to custom implementation
const giftCard = computed(() => {
  // First check for gift card data in Strapi
  const strapiGiftCard = props.product?.strapiData?.giftCard;
  if (strapiGiftCard) return strapiGiftCard;

  // If no gift card in Strapi, return false
  return false;
});

// Format the bonus data amount for display in the bonus tag
const bonusDataAmount = computed(() => {
  // Reuse the bonusDataGbNumber value for consistency
  return bonusDataGbNumber.value > 0 ? `${bonusDataGbNumber.value}GB` : null;
});

// We already have discountAmount handling the price discount, so no need for a separate implementation

// Product data processing

const productRecordId = computed(() => {
  // Get the service template code (e.g., "93-FR")
  return props.product?.offerServiceTemplate[0]?.serviceTemplate.code;
});

// Extract just the numeric portion of the productRecordId for matching with recurring price IDs
const productRecordNumericId = computed(() => {
  // If the code has a numeric part (like "93-FR" → 93), extract it
  const code = productRecordId.value;
  if (code) {
    // Extract just the numeric part if it exists
    const numericPart = code.match(/^(\d+)/);
    if (numericPart && numericPart[1]) {
      return Number(numericPart[1]);
    }
  }
  // Fallback to the full code for cases where there's no numeric prefix
  return code;
});

const priceObject = computed(() => {
  // Add null check for recurring array before using find
  const recurring = props.product?.productOfferingPrice?.recurring || [];

  // First try to find the price using the numeric ID extracted from the product code
  // This handles cases like product code "93-FR" matching recurring price ID "93"
  let findPrice = recurring.find(
    (price) => price.id === productRecordNumericId.value
  );

  // If no match with numeric ID, try matching by name (for backward compatibility)
  if (!findPrice && productRecordId.value) {
    findPrice = recurring.find((price) =>
      price.name?.includes(productRecordId.value)
    );
  }

  // If still no match, just use the first recurring price if available
  if (!findPrice && recurring.length > 0) {
    console.log("No exact price match found, using first recurring price");
    findPrice = recurring[0];
  }

  return findPrice;
});

const finalPrice = computed(() => {
  // Use the improved price calculation logic with better fallbacks
  // Log the product name for debugging when in development mode
  if (process.env.NODE_ENV !== "production") {
    console.log(
      `Calculating FINAL PRICE for product: ${props.product?.name} (${props.product?.code})`
    );
  }

  // First check if there's a recurring price available, otherwise we may need to use oneTime
  // This ensures we don't incorrectly use a $0 oneTime price for affiliate products
  const recurring = props.product?.productOfferingPrice?.recurring || [];
  if (recurring.length > 0 && !priceObject.value) {
    console.log(
      "Warning: No matching recurring price found by ID, but recurring prices exist"
    );
  }

  // Get price from priceObject or fallback to checking if any recurring price exists
  let price;
  if (priceObject.value?.price?.taxIncludedAmount !== undefined) {
    // Use the matched recurring price
    price = priceObject.value.price.taxIncludedAmount;
  } else if (recurring.length > 0) {
    // If we couldn't match a specific recurring price but recurring prices exist, use the first one
    price = recurring[0].price?.taxIncludedAmount ?? 0;
    console.log("Using first available recurring price:", price);
  } else {
    // If no recurring prices at all, fall back to oneTime (though this shouldn't happen for subscription products)
    price =
      props.product?.productOfferingPrice?.oneTime?.[0]?.price
        ?.taxIncludedAmount ?? 0;
    console.log("No recurring prices found, using oneTime price:", price);
  }

  console.log("Original API price (before any adjustments):", price);

  // If the price is a string (from API response), convert it to a number
  if (typeof price === "string" && price !== "") {
    price = parseFloat(price);
  }

  // Ensure price is a valid number or default to 0
  let finalValue = typeof price === "number" && !isNaN(price) ? price : 0;

  // We don't need to add 10% as the API already includes tax
  // Just return the exact price from the API

  return finalValue;
});

// Format the price to only show decimals if not .00
const formattedPrice = computed(() => {
  if (finalPrice.value === 0) return "$0";

  // Check if the price has no decimal part
  if (finalPrice.value % 1 === 0) {
    return "$" + finalPrice.value;
  } else {
    return currency(finalPrice.value);
  }
});

// Helper function to check if a value exists and is greater than 0
const isPositiveNumber = (val) => {
  if (typeof val === "string") {
    return parseFloat(val) > 0;
  }
  return typeof val === "number" && val > 0;
};

// Calculate the discount amount directly from API values
const discountAmount = computed(() => {
  // If we have an explicit discount amount in the API, use it
  if (priceObject.value?.discount) {
    const amount = Number(priceObject.value.price.discountAmount);
    if (amount > 0) {
      console.log("Found explicit discount amount:", amount);
      return amount;
    }
  }

  // Otherwise calculate it from the difference between original and discounted prices
  const originalAmount = priceObject.value?.price?.taxIncludedAmount;
  const discountedAmount =
    priceObject.value?.price?.taxIncludedAmountAfterDiscount;

  if (originalAmount && discountedAmount) {
    const original = Number(originalAmount);
    const discounted = Number(discountedAmount);
    if (original > discounted) {
      const calculatedDiscount = original - discounted;
      console.log(
        "Calculated discount from price difference:",
        calculatedDiscount
      );
      return calculatedDiscount;
    }
  }

  // If there's a discount object but no amount info, default to the discountValue if available
  if (priceObject.value?.discount?.discountValue) {
    console.log(
      "Using discountValue from discount object:",
      priceObject.value.discount.discountValue
    );
    return Number(priceObject.value.discount.discountValue);
  }

  // Finally, check allowedDiscountPlans for price discounts
  const allowedDiscountPlans = props.product?.allowedDiscountPlans;
  if (allowedDiscountPlans && Array.isArray(allowedDiscountPlans)) {
    for (const discountPlan of allowedDiscountPlans) {
      const discountPlanItems = Array.isArray(discountPlan.discountPlanItem)
        ? discountPlan.discountPlanItem
        : [discountPlan.discountPlanItem];

      for (const item of discountPlanItems) {
        if (item?.discountPlanItemType === "FIXED" && item?.discountValue > 0) {
          console.log(
            "Found FIXED discount in allowedDiscountPlans:",
            item.discountValue
          );
          return Number(item.discountValue);
        } else if (
          item?.discountPlanItemType === "PERCENTAGE" &&
          item?.discountValue > 0
        ) {
          // For percentage discounts, calculate based on original price
          const original = Number(originalAmount);
          if (!isNaN(original)) {
            const percentDiscount =
              original * (Number(item.discountValue) / 100);
            console.log(
              "Calculated PERCENTAGE discount in allowedDiscountPlans:",
              percentDiscount
            );
            return percentDiscount;
          }
        }
      }
    }
  }

  return 0;
});

// Check if the product has a valid discount
const hasDiscount = computed(() => {
  // FIRST check if we're in the Change Plan journey - if so, return false immediately
  if (typeof window !== "undefined") {
    const currentUrl = window.location.href || "";
    const isChangePlanJourney =
      currentUrl.includes("/change-plan") ||
      currentUrl.includes("/account/dashboard");
    if (isChangePlanJourney) {
      // Never show discounts in Change Plan journey
      return false;
    }
  }

  // First check if we have the discount object
  if (priceObject.value?.discount) {
    return true;
  }

  // Then check for a discount amount greater than 0
  if (discountAmount.value > 0) {
    return true;
  }

  // Then check if we have an 'after discount' price that differs from the original
  const originalAmount = priceObject.value?.price?.taxIncludedAmount;
  const discountedAmount =
    priceObject.value?.price?.taxIncludedAmountAfterDiscount;

  if (
    originalAmount &&
    discountedAmount &&
    originalAmount !== discountedAmount
  ) {
    console.log("Found different original and discounted amounts");
    return true;
  }

  return false;
});

const originalPrice = computed(() => {
  // Always show the original price even if no discount
  let price = priceObject.value?.price?.taxIncludedAmount;

  if (typeof price === "string" && price !== "") {
    price = parseFloat(price);
  }

  if (typeof price === "number" && !isNaN(price)) {
    // Format the price similar to formattedPrice
    if (price % 1 === 0) {
      return "$" + price;
    } else {
      return currency(price);
    }
  }

  return null;
});

const discountedPrice = computed(() => {
  if (!hasDiscount.value) {
    console.log("No discount available, using regular price");
    return formattedPrice.value;
  }

  let price = priceObject.value?.price?.taxIncludedAmountAfterDiscount;
  console.log("Discounted price from API:", price);

  if (typeof price === "string" && price !== "") {
    price = parseFloat(price);
  }

  if (typeof price === "number" && !isNaN(price)) {
    // No need to add 10% tax, use the exact price from the API
    console.log("Final discounted price from API:", price);

    // Format the price similar to formattedPrice
    if (price % 1 === 0) {
      return "$" + price;
    } else {
      return currency(price);
    }
  }

  return formattedPrice.value; // Fall back to regular price if calculation fails
});

// Feature text gets loaded from the correct template field
// (Promotion description moved to combined implementation above)

// Get the feature text from the correct field
const featureText = computed(() => {
  return (
    props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields?.pim
      ?.goCommerce?.metadataFeatureText || ""
  );
});

const membershipOffer = computed(() => {
  return (
    props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields?.pim
      ?.goCommerce?.membershipOffer || ""
  );
});

// Get bonus data description text to display after price
const bonusDataDescription = computed(() => {
  // FIRST check if we're in the Change Plan journey - if so, return null immediately
  if (typeof window !== "undefined") {
    const currentUrl = window.location.href || "";
    const isChangePlanJourney =
      currentUrl.includes("/change-plan") ||
      currentUrl.includes("/account/dashboard");
    if (isChangePlanJourney) {
      // Never show bonus data description in Change Plan journey
      return null;
    }
  }

  // Get bonus data description from metadata
  const metadataDesc =
    props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields?.pim
      ?.goCommerce?.metadataBonusDataDescription;

  // Return the description directly if available
  if (metadataDesc) return metadataDesc;

  // Only use a fallback if there's actual bonus data and no description
  return bonusDataGbNumber.value > 0
    ? `Includes ${bonusDataGbNumber.value}GB bonus data`
    : null;
});

// Helper function to display plan type based on code
const getPlanTypeDisplay = (code) => {
  if (!code) return "";

  switch (code) {
    case "MONTH-28DAYS":
      return "28 Days";
    case "MONTH-90DAYS":
      return "90 Days";
    case "MONTH_1":
    case "MONTH":
      return "Month";
    case "ANNUAL":
      return "365 Days";
    default:
      return code;
  }
};

// Get the discount details with improved error handling
const discountDetails = computed(() => {
  // FIRST check if we're in the Change Plan journey - if so, return null immediately
  if (typeof window !== "undefined") {
    const currentUrl = window.location.href || "";
    const isChangePlanJourney =
      currentUrl.includes("/change-plan") ||
      currentUrl.includes("/account/dashboard");
    if (isChangePlanJourney) {
      // Never show discount details in Change Plan journey
      return null;
    }
  }

  // CRITICAL: Check if this is product 86 ($30 45GB) and hide ALL discount details in Change Plan journey
  const productObj = props.product || {};
  const productAsAny = productObj as any;

  // Check multiple ways to identify product 86
  const isProduct86 =
    productObj.code === "86" ||
    productAsAny.serviceTemplateCode === "86" ||
    productObj.offerServiceTemplate?.[0]?.serviceTemplate?.code === "86" ||
    productObj.productOfferingPrice?.recurring?.[0]?.id === 86 ||
    productObj.name?.includes("$30 45GB");

  // If we're in the Change Plan journey and this is product 86, return null to hide all discount details
  if (
    isProduct86 &&
    window.location.pathname.includes("/my-plans/change-plan")
  ) {
    console.log(
      "🔴 HIDING discount offer details for product 86 in Change Plan journey"
    );
    return null; // Return null to prevent displaying any discount details
  }

  // Default behavior - Get discount details as normal
  let discount = null;
  if (priceObject?.value?.discount) {
    // Add null check for allowedDiscountPlans before using find
    const allowedDiscountPlans = props.product?.allowedDiscountPlans || [];
    discount = allowedDiscountPlans.find(
      (discount) =>
        discount.code === priceObject?.value?.discount?.discountPlanCode
    );

    // Set a default description if not provided
    if (discount && discountAmount.value > 0) {
      if (!discount.description) {
        const duration = discount.defaultDuration || 90;
        const unit = discount.durationUnit === "DAY" ? "days" : "months";
        discount.description = `$${Math.round(discountAmount.value)} off for ${duration > 30 ? Math.round(duration / 30) : duration} ${unit}`;
      }
    }
  }
  return discount;
});

const rppPrice = computed(() => {
  return priceObject?.value?.price?.taxIncludedAmount || 0;
});

const durationUnit = computed(() => {
  // First try direct renewForUnit
  const directUnit =
    props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
      ?.renewForUnit;
  if (directUnit) return directUnit;

  // If initial term type is CALENDAR, check calendar units
  const initialTermType =
    props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
      ?.initialTermType;
  if (initialTermType === "CALENDAR") {
    const calendarInfo =
      props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
        ?.calendarInitialyActiveFor;
    if (calendarInfo?.days) return "DAY";
    if (calendarInfo?.months) return "MONTH";
    if (calendarInfo?.years) return "YEAR";
  }

  // Fallback - extract from product description
  const description = props.product?.description || "";

  // Use regex to extract duration values from description
  const dayMatch = description.match(/\b\d+\s*DAY(?:S)?\b/i);
  if (dayMatch && dayMatch[1]) {
    return "DAY";
  }

  const monthMatch = description.match(/\b\d+\s*MONTH(?:S)?\b/i);
  if (monthMatch && monthMatch[1]) {
    return "MONTH";
  }

  const yearMatch = description.match(/\b\d+\s*YEAR(?:S)?\b/i);
  if (yearMatch && yearMatch[1]) {
    return "YEAR";
  }

  // Specific common cases
  if (description.match(/365\b/i)) {
    return "DAY"; // 365 typically means 365 days
  }

  // Default to DAY if we can't determine otherwise
  return "DAY";
});

const renewalDuration = computed(() => {
  // First try direct renewFor
  const directDuration =
    props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
      ?.renewFor;
  if (directDuration) return directDuration;

  // Check calendar duration values
  const calendarInfo =
    props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
      ?.calendarInitialyActiveFor;
  if (calendarInfo) {
    if (calendarInfo.days) return calendarInfo.days;
    if (calendarInfo.months) return calendarInfo.months;
    if (calendarInfo.years) return calendarInfo.years;
  }

  // Extract from product name/description
  const description = props.product?.description || "";
  const dayMatch = description.match(/\b(\d+)\s*DAY\b/i);
  if (dayMatch) return dayMatch[1];

  const monthMatch = description.match(/\b(\d+)\s*MONTH\b/i);
  if (monthMatch) return monthMatch[1];

  const yearMatch = description.match(/\b(\d+)\s*YEAR\b/i);
  if (yearMatch) return yearMatch[1];

  // Check for 365 in the name, common for yearly plans expressed in days
  if (description.match(/\b365\b/)) return 365;

  return null;
});

const goCommerce = computed(() => {
  return props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields
    ?.pim?.goCommerce;
});

const planDurationText = computed(() => {
  // Enhanced duration display logic
  const duration = renewalDuration.value;
  const unit = durationUnit.value;

  // Add extra logging to help debug the values being used
  console.log("Plan duration values:", {
    durationUnit: unit,
    renewalDuration: duration,
    productDescription: props.product?.description,
    calendarInfo:
      props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
        ?.calendarInitialyActiveFor,
  });

  // First check standard duration/unit combinations
  if (unit === "MONTH" && duration) {
    return duration > 1 ? `${duration} months` : `month`;
  } else if (unit === "DAY" && duration) {
    return `${duration} days`;
  } else if (unit === "YEAR" && duration) {
    return duration > 1 ? `${duration} years` : `year`;
  }

  // For products with special durations in the description
  const description = props.product?.description || "";

  // Use regex to extract duration values from description
  const dayMatch = description.match(/\b(\d+)\s*DAY(?:S)?\b/i);
  if (dayMatch && dayMatch[1]) {
    return `${dayMatch[1]} days`;
  }

  const monthMatch = description.match(/\b(\d+)\s*MONTH(?:S)?\b/i);
  if (monthMatch && monthMatch[1]) {
    const months = parseInt(monthMatch[1], 10);
    return months > 1 ? `${months} months` : "month";
  }

  const yearMatch = description.match(/\b(\d+)\s*YEAR(?:S)?\b/i);
  if (yearMatch && yearMatch[1]) {
    const years = parseInt(yearMatch[1], 10);
    return years > 1 ? `${years} years` : "year";
  }

  // Specific common cases
  if (description.match(/365\b/i)) {
    return "365 days";
  }
  if (description.match(/28\b/i)) {
    return "28 days";
  }
  if (description.match(/30\b/i)) {
    return "30 days";
  }
  if (description.match(/90\b/i)) {
    return "90 days";
  }

  // Default fallback
  return "28 days";
});

// Gift card implementation moved to combined implementation above

const getDestinationCount = (destinations: string | undefined): number => {
  if (!destinations) return 0;
  const parts = destinations
    .split(/,|\sand\s/)
    .filter((part: string) => part.trim());
  return parts.length;
};

const { currency } = formatterUtils();
const dayjs = useDayjs();
</script>

<template>
  <div
    :class="['card-outer', isBestValue ? 'has-best-value' : 'standard-card']"
  >
    <div class="card-inner">
      <!-- Best value tag positioned to break through the top border -->
      <div v-if="isBestValue" class="best-value-tag">best value</div>
      <!-- Tags section -->
      <div class="tags-section">
        <!-- Top row with tags: discount and bonus data -->
        <div class="flex justify-between">
          <!-- Left side tags container - discount tag only -->
          <div class="flex gap-2">
            <!-- Discount tag - only shown if discount amount exists and is not 0 -->
            <div v-if="hasDiscount && discountAmount > 0" class="discount-tag">
              save ${{ Math.round(discountAmount * 100) / 100 }}
            </div>
            <div v-else class="discount-tag-placeholder"></div>
          </div>

          <!-- Right side - Bonus Data tag now positioned where best-value-tag used to be -->
          <div v-if="bonusDataAmount" class="bonus-data-tag">
            +{{ bonusDataAmount }} bonus
          </div>
        </div>
        <!-- Data amount first, then price -->
        <div class="data-amount">{{ quotaDataGb }}GB Data</div>
        <div class="plan-title">
          <div class="price-display">
            <!-- Single dynamic approach for all products -->
            <!-- When discount exists AND is greater than 0, show original price with strikethrough + discounted price -->
            <template
              v-if="
                hasDiscount &&
                discountAmount > 0 &&
                originalPrice &&
                discountedPrice
              "
            >
              <del class="original-price">{{ originalPrice }}</del
              >&nbsp;{{ discountedPrice
              }}<span class="period"
                >/{{
                  getPlanTypeDisplay(
                    props.product?.offerServiceTemplate?.[0]?.serviceTemplate
                      ?.renewalRule?.calendarInitialyActiveFor?.code
                  )
                }}</span
              >
            </template>
            <!-- Regular price for non-discounted products -->
            <template v-else>
              {{ formattedPrice
              }}<span class="period"
                >/{{
                  getPlanTypeDisplay(
                    props.product?.offerServiceTemplate?.[0]?.serviceTemplate
                      ?.renewalRule?.calendarInitialyActiveFor?.code
                  )
                }}</span
              >
            </template>
          </div>
        </div>

        <!-- Bonus data description display directly after price -->
        <div v-if="bonusDataDescription" class="bonus-data-description">
          {{ bonusDataDescription }}
        </div>

        <!-- Offer details moved to footer -->

        <!-- Promo description moved to footer -->
        <div v-if="membershipOffer && !isChangePlan" class="feature-item">
          <RawHtmlRenderer
            v-if="membershipOffer"
            :htmlContent="membershipOffer"
          />
        </div>

        <!-- Features section -->
        <div class="features-section">
          <!-- Plan features section -->
          <div v-if="featureText" class="plan-features">
            <div class="includes-title">This plan includes:</div>
            <div class="feature-text">
              <!-- Using RawHtmlRenderer for complex HTML content during OnePass Mobile migration -->
              <RawHtmlRenderer v-if="featureText" :htmlContent="featureText" />
            </div>
          </div>

          <div
            v-for="(feature, index) in productFeatures"
            :key="feature.id"
            class="feature-item"
          >
            <NuxtImg
              v-if="feature?.icon"
              provider="strapi"
              :src="feature.icon.url"
              class="feature-icon"
              width="24"
              height="24"
            />
            <div>
              <div v-if="!feature.destinations" class="text-black">
                {{ feature.name }}
              </div>
              <BaseTooltip
                v-if="feature.destinations"
                :tooltip="feature.destinations"
              >
                <!-- Fixed URL handling to prevent object URL errors -->
                <span class="text-black underline cursor-pointer"
                  >{{ getDestinationCount(feature.destinations) }} selected
                  destinations</span
                >
              </BaseTooltip>
            </div>
          </div>
          <!-- SLOT for SELECT button directly after features -->
          <slot name="after-features"></slot>
        </div>

        <!-- More Info button - only shown when ctaButton prop is true AND we're not on a product page (props.buyNow is present on product pages) -->
        <div v-if="props.ctaButton && !props.buyNow" class="text-center mb-4">
          <button
            class="more-info-btn"
            @click="
              $router.push(typeof props.link === 'string' ? props.link : '#')
            "
          >
            More Info
          </button>
        </div>

        <!-- Buy Now button - only shown on product pages when props.buyNow is provided -->
        <!-- Never show in Change Plan journey or any dashboard context -->
        <div
          v-if="shouldShowBuyNowButton && typeof props.buyNow === 'function'"
          class="text-center mb-4"
        >
          <button class="buy-now-btn" @click="props.buyNow">BUY NOW</button>
        </div>

        <div class="card-details-section">
          <!-- Slot for content to appear right before critical-info -->
          <slot name="critical-info-prepend"></slot>
          <div class="critical-info">
            <a href="/critical-information">Critical Information Summary</a
            ><br />
            All for use in Australia<br />
            Optus Fair Go Policy applies
          </div>

          <!-- Promotion text now positioned after critical-info -->
          <div class="promotion-text-section" v-if="promotionDescription">
            <div class="promotion-text" v-html="promotionDescription"></div>
          </div>

          <!-- Offer details after promotion text -->
          <div class="offer-details-section" v-if="discountDetails">
            Offer ends:
            {{ dayjs(discountDetails.endDate).format("DD/MM/YYYY") }}.<br />
            {{ discountDetails.description }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.card-outer {
  height: 100%;
  width: 100%;
  margin-bottom: 20px;

  /* Mobile responsive adjustments */
  @media (max-width: 767px) {
    width: 100%;
    margin-right: 0;
  }
}

.membership-offer-feature-text {
  font-size: 14px;
  color: #333;
  line-height: 1.4;
  font-weight: normal;
}

/* Black border for best value cards */
.has-best-value .card-inner {
  border: 2px solid #000;
  box-shadow: 0 4px 8px rgba(153, 0, 241, 0.15);
}

/* Slightly larger grey border for standard cards */
.standard-card .card-inner {
  border: 1.5px solid #e0e0e0;
}

.card-inner {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 24px 20px 16px;
  background-color: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  position: relative; /* Needed for absolute positioning of best-value-tag */
  margin-top: 10px; /* Space for the tag that breaks through */
}

.plan-title {
  display: flex;
  flex-direction: column;
  margin-bottom: 2px;
}

.price-display {
  font-size: 32px;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
  font-family: "Sora", sans-serif;
}

.period {
  font-size: 18px;
  font-weight: 400;
  color: #666;
  font-family: Sora, sans-serif;
}

.discount-tag {
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  background-color: #b0f4de;
  color: #333;
}

.discount-tag-placeholder {
  height: 28px; /* Same height as discount-tag */
}

/* Bonus Data tag styling */
.bonus-data-tag {
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  background-color: #3a016a; /* Darker OnePass purple */
  color: white;
}

.original-price {
  font-size: 22px;
  color: #8f0202; /* Dark red for the slashed price */
  text-decoration: none; /* Remove standard strikethrough */
  margin-right: 6px;
  font-weight: 400;
  position: relative;
  display: inline-block;
}

/* Custom diagonal slash from bottom-left to top-right */
.original-price::after {
  content: "";
  position: absolute;
  left: -2px;
  bottom: 45%;
  width: calc(100% + 6px);
  height: 2.5px;
  background-color: #8f0202; /* Dark red slash to match the text */
  transform: rotate(-20deg); /* Diagonal line */
}

.tags-section {
  margin-bottom: 16px;
}

.data-amount {
  font-size: 28px;
  font-weight: 700;
  color: #333;
  text-align: left;
  margin: 0 0 6px; /* Reduced from 12px to 6px */
  font-family: "Sora", sans-serif;
}

.giftCard {
  :deep(p) {
    font-weight: 600;
    font-size: 1rem;
  }
}

.more-info-btn {
  width: 100%;
  background-color: #9900f1; /* Primary color */
  color: white;
  padding: 0.75rem 0;
  border-radius: 0.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  transition: background-color 0.2s ease;
}

.more-info-btn:hover {
  background-color: #8800d4; /* Slightly darker shade for hover */
}

.buy-now-btn {
  width: 100%;
  background-color: #9900f1; /* Primary color */
  color: white;
  padding: 0.75rem 0;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  text-transform: uppercase;
  box-shadow: 0 4px 6px rgba(153, 0, 241, 0.25); /* drop shadow */
  transition: all 0.2s ease;
}

.buy-now-btn:hover {
  background-color: #8800d4; /* Slightly darker shade for hover */
  transform: translateY(-1px);
  box-shadow: 0 6px 8px rgba(153, 0, 241, 0.3); /* larger shadow on hover */
}

.plan-type {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.card-outer {
  height: 100%;
  width: 100%;
  margin-bottom: 20px;

  /* Mobile responsive adjustments */
  @media (max-width: 767px) {
    width: 100%;
    margin-right: 0;
  }
}

/* Black border for best value cards */
.has-best-value .card-inner {
  border: 2px solid #000;
  box-shadow: 0 4px 8px rgba(153, 0, 241, 0.15);
}

/* Slightly larger grey border for standard cards */
.standard-card .card-inner {
  border: 1.5px solid #e0e0e0;
}

.card-inner {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 24px 20px 16px;
  background-color: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  position: relative; /* Needed for absolute positioning of best-value-tag */
  margin-top: 10px; /* Space for the tag that breaks through */
}

.best-value-tag {
  background-color: #9900f1; /* OnePass purple color - matches brand guidelines */
  color: white;
  padding: 4px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  position: absolute;
  top: -11px; /* Precise positioning to break through the top border */
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  white-space: nowrap;
  text-align: center;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);

  /* Mobile optimizations */
  @media (max-width: 767px) {
    font-size: 12px;
    padding: 3px 16px;
  }
}

.description {
  background-color: #f0f0f0;
  color: #666;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: bold;
  margin-top: 6px;
  display: inline-block;
}

.plan-features {
  margin-bottom: 16px;

  .includes-title {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 8px;
    color: #333;
  }

  .feature-text {
    font-size: 14px;
    color: #333;
    line-height: 1.4;
    font-weight: normal;
  }
}

/* Offer details section styling to match screenshot exactly */
.bonus-data-description {
  font-size: 14px;
  color: #333;
  text-align: center;
  margin-top: 8px;
  margin-bottom: 12px;
  line-height: 1.5;
  max-width: 100%;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;

  /* Ensure mobile/desktop separation */
  @media (max-width: 767px) {
    font-size: 13px;
    padding: 3px 6px;
    margin-top: 6px;
    margin-bottom: 10px;
  }
}

.offer-details {
  font-size: 14px;
  color: #333;
  text-align: left;
  margin-top: 8px;
  margin-bottom: 12px;
  line-height: 1.5;
  max-width: 100%;
  overflow: hidden;
}

/* Offer details section styling - matching critical-info */
.offer-details-section {
  font-size: 12px;
  color: #666;
  text-align: center;
  margin-top: 16px;
  line-height: 1.4;
  max-width: 100%;
}

/* Promotion text section with horizontal line */
.promotion-text-section {
  width: 100%;
  padding-top: 8px;
  margin-top: 16px;
  margin-bottom: 8px;
  text-align: center;
}

/* Promotion text styling - now consistent with critical-info */
.promotion-text {
  font-size: 12px;
  color: #666;
  margin-top: 0;
  margin-bottom: 8px;
  line-height: 1.4;
}

/* Gift card container styling */
.gift-card-container {
  display: flex;
  align-items: center;
  margin-top: 12px;
  margin-bottom: 12px;
}

.gift-card-icon {
  width: 40px;
  height: 40px;
  margin-right: 12px;
}

.gift-card-text {
  font-size: 14px;
  color: #333;
}

.membership-offer {
  .feature-text {
    font-size: 14px;
    color: #333;
    line-height: 1.4;
    font-weight: normal;
  }
}
/* Features section */
.features-section {
  width: 100%;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
  // margin-top: 12px;
  padding-bottom: 16px;
  flex: 1; /* Take up available space */
}

.feature-item {
  display: flex;
  gap: 16px;
  margin-top: 8px;
  align-items: flex-start;
}

.feature-icon {
  width: 24px;
  height: 24px;
  margin-top: 4px;
  flex-shrink: 0;
}

.card-details-section {
  width: 100%;
  text-align: center;
  padding-top: 4px;
  padding-bottom: 4px;
  border-top: 1px solid #eee;
  margin-top: auto; /* Push footer to bottom of flex container */
}

.critical-info {
  text-align: center;
  font-size: 12px;
  line-height: 1.4;
  color: #666;
  margin-top: 16px;

  a {
    color: #9900f1;
    text-decoration: underline;
    font-weight: 500;
  }
}
</style>
